import { useEffect, useState, type FormEvent } from "react";
import { apiRequest } from "../api";
import { Button, Field, Shell, StatusPill } from "../components";
import { setPageMetadata } from "../page-metadata";

interface RecoveryChallenge {
  challengeId: string;
  pollSecret: string;
  paymentUrl: string;
  expiresAt: string;
  message: string;
}

type RecoveryStatus = "PENDING" | "VERIFIED" | "FAILED" | "EXPIRED";
type RecoveryMethod = "secret" | "alipay" | "email";
type MessageTone = "success" | "error";

const RECOVERY_TABS: Array<{ id: RecoveryMethod; label: string }> = [
  { id: "secret", label: "恢复暗号" },
  { id: "alipay", label: "支付宝身份" },
  { id: "email", label: "恢复邮箱" },
];

export function RecoveryPage() {
  const [method, setMethod] = useState<RecoveryMethod>("secret");
  const [username, setUsername] = useState("");
  const [recoverySecret, setRecoverySecret] = useState("");
  const [email, setEmail] = useState("");
  const [challenge, setChallenge] = useState<RecoveryChallenge | null>(() => {
    const value = sessionStorage.getItem("cheemspay-recovery");
    return value ? (JSON.parse(value) as RecoveryChallenge) : null;
  });
  const [status, setStatus] = useState<RecoveryStatus>("PENDING");
  const [resetToken, setResetToken] = useState(
    () => new URLSearchParams(window.location.search).get("token") ?? "",
  );
  const [newPassword, setNewPassword] = useState("");
  const [message, setMessage] = useState("");
  const [messageTone, setMessageTone] = useState<MessageTone>("success");
  const [busy, setBusy] = useState(false);
  const [resetComplete, setResetComplete] = useState(false);

  useEffect(() => {
    setPageMetadata(
      "找回账号 | CheemsPay",
      "通过恢复暗号、已验证邮箱或历史支付宝付款身份重置 CheemsPay 密码。",
    );
  }, []);

  useEffect(() => {
    if (!challenge || status !== "PENDING") {
      return;
    }
    let cancelled = false;
    const poll = async () => {
      while (!cancelled) {
        await delay(2_500);
        if (cancelled) {
          return;
        }
        const result = await apiRequest<{ status: RecoveryStatus }>(
          "/api/recovery/status",
          {
            method: "POST",
            body: JSON.stringify({
              challengeId: challenge.challengeId,
              pollSecret: challenge.pollSecret,
            }),
          },
        ).catch(() => null);
        if (result && result.status !== "PENDING") {
          setStatus(result.status);
          return;
        }
      }
    };
    void poll();
    return () => {
      cancelled = true;
    };
  }, [challenge, status]);

  const start = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      const created = await apiRequest<RecoveryChallenge>(
        "/api/recovery/start",
        {
          method: "POST",
          body: JSON.stringify({ username }),
        },
      );
      setChallenge(created);
      setStatus("PENDING");
      sessionStorage.setItem(
        "cheemspay-recovery",
        JSON.stringify(created),
      );
      window.open(created.paymentUrl, "_blank", "noopener,noreferrer");
    } catch (error) {
      setMessageTone("error");
      setMessage(error instanceof Error ? error.message : "无法开始找回");
    } finally {
      setBusy(false);
    }
  };

  const startEmailRecovery = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      await apiRequest("/api/recovery/email", {
        method: "POST",
        body: JSON.stringify({ email }),
      });
      setMessage(
        "若该地址是已验证的恢复邮箱，改密邮件已经进入发送队列。为保护账号，系统不会披露邮箱是否存在。",
      );
      setMessageTone("success");
    } catch (error) {
      setMessageTone("error");
      setMessage(error instanceof Error ? error.message : "无法开始邮箱找回");
    } finally {
      setBusy(false);
    }
  };

  const startSecretRecovery = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      const result = await apiRequest<{ resetToken: string }>(
        "/api/recovery/secret",
        {
          method: "POST",
          body: JSON.stringify({ username, recoverySecret }),
        },
      );
      setResetToken(result.resetToken);
    } catch (error) {
      setMessageTone("error");
      setMessage(
        error instanceof Error ? error.message : "恢复暗号无效或不可用于该账号",
      );
    } finally {
      setBusy(false);
    }
  };

  const claim = async () => {
    if (!challenge) {
      return;
    }
    setBusy(true);
    setMessage("");
    try {
      const result = await apiRequest<{ resetToken: string }>(
        "/api/recovery/claim-reset",
        {
          method: "POST",
          body: JSON.stringify({
            challengeId: challenge.challengeId,
            pollSecret: challenge.pollSecret,
          }),
        },
      );
      setResetToken(result.resetToken);
    } catch (error) {
      setMessageTone("error");
      setMessage(error instanceof Error ? error.message : "无法领取改密凭证");
    } finally {
      setBusy(false);
    }
  };

  const reset = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      await apiRequest("/api/auth/reset-password", {
        method: "POST",
        body: JSON.stringify({ newPassword, token: resetToken }),
      });
      sessionStorage.removeItem("cheemspay-recovery");
      setChallenge(null);
      setResetToken("");
      setResetComplete(true);
    } catch (error) {
      setMessageTone("error");
      setMessage(error instanceof Error ? error.message : "改密失败");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell back={{ href: "/", label: "返回首页" }}>
      <section className="recovery-layout">
        <div className="recovery-copy">
          <h1>找回账号</h1>
          <p>
            未购买前可用恢复暗号。开通授权后，改用已验证邮箱，或用历史付款的同一支付宝（0.01 元验证，不退款）。
          </p>
          <p className="privacy-note">
            无论账号是否存在，页面都会显示相同的流程。
          </p>
        </div>

        <div aria-busy={busy} className="recovery-card">
          <div className="recovery-card-head">
            <h2>
              {resetComplete
                ? "密码已更新"
                : resetToken
                  ? "设置新密码"
                  : "账号找回"}
            </h2>
            {challenge ? (
              <StatusPill
                tone={
                  status === "VERIFIED"
                    ? "good"
                    : status === "PENDING"
                      ? "warn"
                      : "neutral"
                }
              >
                {status === "PENDING"
                  ? "等待验证"
                  : status === "VERIFIED"
                    ? "身份已匹配"
                    : status === "FAILED"
                      ? "未能匹配"
                      : "挑战已过期"}
              </StatusPill>
            ) : null}
          </div>

          {resetComplete ? (
            <div className="verified-box" role="status">
              <div className="checkmark">✓</div>
              <h3>现在可以重新登录</h3>
              <p>密码已更新，旧会话均已失效。请使用新密码登录。</p>
              <a className="button anchor-button" href="/">
                返回登录
              </a>
            </div>
          ) : resetToken ? (
            <form onSubmit={reset}>
              <Field label="新密码" hint="至少 10 位；更新后会吊销其他登录会话">
                <input
                  minLength={10}
                  onChange={(event) => setNewPassword(event.target.value)}
                  required
                  type="password"
                  value={newPassword}
                />
              </Field>
              <Button disabled={busy} type="submit">
                {busy ? "正在更新…" : "更新密码并返回登录"}
              </Button>
            </form>
          ) : !challenge ? (
            <>
              <div className="mode-tabs" role="tablist">
                {RECOVERY_TABS.map((tab) => (
                  <button
                    aria-controls={`recovery-panel-${tab.id}`}
                    aria-selected={method === tab.id}
                    className={method === tab.id ? "active" : ""}
                    disabled={busy}
                    id={`recovery-tab-${tab.id}`}
                    key={tab.id}
                    onClick={() => setMethod(tab.id)}
                    onKeyDown={(event) => {
                      if (
                        !["ArrowRight", "ArrowLeft", "Home", "End"].includes(
                          event.key,
                        )
                      ) {
                        return;
                      }
                      const currentIndex = RECOVERY_TABS.findIndex(
                        ({ id }) => id === tab.id,
                      );
                      const nextIndex =
                        event.key === "ArrowRight"
                          ? (currentIndex + 1) % RECOVERY_TABS.length
                          : event.key === "ArrowLeft"
                            ? (currentIndex - 1 + RECOVERY_TABS.length) %
                              RECOVERY_TABS.length
                            : event.key === "Home"
                              ? 0
                              : event.key === "End"
                                ? RECOVERY_TABS.length - 1
                                : currentIndex;
                      event.preventDefault();
                      if (nextIndex === currentIndex) {
                        return;
                      }
                      const nextTab = RECOVERY_TABS[nextIndex];
                      setMethod(nextTab.id);
                      document
                        .getElementById(`recovery-tab-${nextTab.id}`)
                        ?.focus();
                    }}
                    role="tab"
                    tabIndex={method === tab.id ? 0 : -1}
                    type="button"
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              {RECOVERY_TABS.map((tab) => (
                <div
                  aria-labelledby={`recovery-tab-${tab.id}`}
                  hidden={method !== tab.id}
                  id={`recovery-panel-${tab.id}`}
                  key={tab.id}
                  role="tabpanel"
                  tabIndex={0}
                >
                {tab.id === "secret" ? (
                  <form onSubmit={startSecretRecovery}>
                    <Field label="原用户名">
                      <input
                        minLength={3}
                        onChange={(event) => setUsername(event.target.value)}
                        placeholder="cheems.user"
                        required
                        value={username}
                      />
                    </Field>
                    <Field
                      label="你为此账号设置的专属恢复暗号是什么？"
                      hint="仅适用于从未获得过付费权益的账号"
                    >
                      <input
                        minLength={12}
                        onChange={(event) =>
                          setRecoverySecret(event.target.value)
                        }
                        required
                        type="password"
                        value={recoverySecret}
                      />
                    </Field>
                    <Button disabled={busy} type="submit">
                      {busy ? "正在验证…" : "验证恢复暗号"}
                    </Button>
                  </form>
                ) : tab.id === "alipay" ? (
                  <form onSubmit={start}>
                    <Field label="原用户名">
                      <input
                        minLength={3}
                        onChange={(event) => setUsername(event.target.value)}
                        placeholder="cheems.user"
                        required
                        value={username}
                      />
                    </Field>
                    <div className="privacy-note">
                      请确认用户名正确，并使用该账号历史付款时的同一支付宝。0.01
                      元为身份验证服务费，支付后不退款；验证失败或挑战过期也不退款。
                    </div>
                    <Button disabled={busy} type="submit">
                      {busy ? "正在创建…" : "支付 0.01 元验证（不退款）"}
                    </Button>
                  </form>
                ) : (
                  <form onSubmit={startEmailRecovery}>
                    <Field
                      label="已验证恢复邮箱"
                      hint="只发送一次性改密链接，不参与日常登录"
                    >
                      <input
                        autoComplete="email"
                        onChange={(event) => setEmail(event.target.value)}
                        placeholder="you@example.com"
                        required
                        type="email"
                        value={email}
                      />
                    </Field>
                    <Button disabled={busy} type="submit">
                      {busy ? "正在提交…" : "发送改密邮件"}
                    </Button>
                  </form>
                )}
                </div>
              ))}
            </>
          ) : status === "PENDING" ? (
            <div className="challenge-pending">
              <div className="radar" aria-hidden="true">
                <span />
              </div>
              <h3>正在等待支付宝确认</h3>
              <p>{challenge.message}</p>
              <a
                className="button anchor-button"
                href={challenge.paymentUrl}
                rel="noreferrer"
                target="_blank"
              >
                重新打开 0.01 元验证页（不退款）
              </a>
              <small>
                有效期至 {new Date(challenge.expiresAt).toLocaleTimeString()}
              </small>
            </div>
          ) : status === "VERIFIED" && !resetToken ? (
            <div className="verified-box">
              <div className="checkmark">✓</div>
              <h3>历史付款身份匹配成功</h3>
              <p>
                0.01 元身份验证服务费已收取且不退款。领取一次性改密凭证后，本挑战不能再次使用。
              </p>
              <Button disabled={busy} onClick={() => void claim()}>
                {busy ? "正在领取…" : "领取改密凭证"}
              </Button>
            </div>
          ) : (
            <div className="verified-box">
              <h3>无法完成自动找回</h3>
              <p>
                可能是挑战已过期、账号没有历史付款，或当前支付宝身份与历史记录不同。0.01
                元身份验证服务费支付后不退款。
              </p>
              <Button
                disabled={busy}
                onClick={() => {
                  sessionStorage.removeItem("cheemspay-recovery");
                  setChallenge(null);
                  setStatus("PENDING");
                }}
              >
                重新尝试
              </Button>
            </div>
          )}
          {message ? (
            <div
              className={messageTone === "error" ? "form-error" : "form-message"}
              role={messageTone === "error" ? "alert" : "status"}
            >
              {message}
            </div>
          ) : null}
        </div>
      </section>
    </Shell>
  );
}

function delay(milliseconds: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, milliseconds);
  });
}
